import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, Injector, input, output, signal, untracked, viewChild } from '@angular/core';
import { Button2Component } from '@frontendlib/components/components/button/button2.component';
import { IconComponent } from '@frontendlib/components/components/icon/icon.component';
import { BoardEmptyStateComponent } from '../board-empty-state/board-empty-state.component';
import { DragNDropModule } from '@modules/drag-n-drop/drag-n-drop.module';
import { type DragData } from '@modules/drag-n-drop/types/drag-data';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import { canDropOnBoard, dropOnBoard } from '../../../drag/board-stitch-drop';
import { BoardListStore } from '../../../services/board-list.store';
import { ToasterService } from '@frontendlib/common/features/toaster/services/toaster.service';
import { BoardsStore, isTempBoard } from '../../../services/boards.store';
import { BOARD_ATTACHMENTS_MENU_STATE_VARIANT, BoardAttachmentsComponent } from '../../../files/board-attachments.component';
import { PermissionsService } from '@frontendlib/common/features/permissions/services/permissions.service';
import { TabsComponent } from '@modules/common/components/tabs/tabs.component';
import { WorkspaceIdentityService } from '@modules/common/services/workspace-identity.service';
import { APPLICATION_SCOPE_TOKEN, ApplicationScope } from '@modules/common/types/ApplicationScope';
import { BoardContentFlushService } from '../../../services/board-content-flush.service';
import { BoardCommentRequestsService, BoardCommentTarget } from '../../../services/board-comment-requests.service';
import { CommentsModule } from '@modules/comments/comments.module';
import { type CommentThreadPin } from '@modules/comments/types/comment';
import { EntityHubSearchComponent } from '@modules/elements/components/entity-hub/entity-hub-search/entity-hub-search.component';
import { FormControlsModule } from '@modules/form-controls/form-controls.module';
import { type Actions, type Subjects, ViewSubjects } from '@sharedlib/common/permissions/types/Permissions';
import { injectBoardGeneralCommentsCount } from '../../../comments/board-general-count';
import { injectBoardAnnotations } from '../../../comments/board-annotations.store';
import { annotationRemoved } from '../../../comments/board-annotations.util';
import { BoardKnowledgeComponent } from '../../../knowledge/board-knowledge.component';
import { BoardKnowledgeVisibilityService } from '../../../knowledge/board-knowledge-visibility.service';
import { BoardVersionHistoryComponent } from '../../../history/board-version-history.component';
import { injectDeckTabCounts, storedStitchListState } from '@modules/slides/utils/deck-tab-counts.util';
import { type BoardTab, boardTabs } from '../../../knowledge/board-tabs.util';
import { type BoardTabOutcome, type BoardTabRequest, BoardsItemTabs } from '../../../ui-commands/boards-item-tabs';
import { BoardConnectionsComponent } from '../../../stitch/board-connections.component';
import { BoardRecordHeadingComponent } from './board-heading/board-record-heading.component';
import { BoardRecordTabComponent } from './board-tab/board-record-tab.component';
import { BoardDetailsComponent } from '../../record/board-details/board-details.component';
import { toBoardStitchSummary } from '../../shared/board-stitch-summary.util';
import { injectBoardOpener } from '../../shared/board-opener';

/**
 * The record view of a board beside the library: its header (title, archive, trash, More, Open editor, Close), a Trash
 * banner and the record tabs (Details, Attachments, Stitch, Comments) under the Knowledge block. Adapted from `SlidesPreviewComponent`;
 * its slide canvas, version history and sharing tabs belong to later relays.
 */
@Component({
  selector: 'app-boards-preview',
  templateUrl: './boards-preview.component.html',
  styleUrl: './boards-preview.component.less',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block h-full' },
  providers: [WorkspaceIdentityService, BoardCommentTarget],
  imports: [
    BoardAttachmentsComponent,
    BoardVersionHistoryComponent,
    BoardConnectionsComponent,
    DatePipe,
    BoardRecordHeadingComponent,
    BoardRecordTabComponent,
    BoardDetailsComponent,
    BoardKnowledgeComponent,
    CommentsModule,
    DragNDropModule,
    EntityHubSearchComponent,
    FormControlsModule,
    TabsComponent,
    Button2Component,
    IconComponent,
    BoardEmptyStateComponent,
  ],
})
export class BoardsPreviewComponent {
  public readonly boardId = input<string | null>(null);
  public readonly closed = output<void>();
  protected readonly store = inject(BoardsStore);
  private readonly injector = inject(Injector);
  private readonly openBoard = injectBoardOpener();
  private readonly list = inject(BoardListStore, { optional: true });
  private readonly toaster = inject(ToasterService);
  protected readonly copying = signal(false);
  private readonly details = viewChild(BoardDetailsComponent);
  protected readonly spaceContext = inject(SpaceContextStore);
  private readonly scope = inject(APPLICATION_SCOPE_TOKEN, { optional: true });
  private readonly visibility = inject(BoardKnowledgeVisibilityService);
  private readonly permissionsService: PermissionsService<Actions, Subjects> = inject(PermissionsService);
  protected readonly permissionsLoaded = this.permissionsService.hasPermissionsLoaded;
  protected readonly hasKnowledgeHubAccess = computed(() => this.permissionsService.checkPermission('read', ViewSubjects.knowledgeHubView));
  protected readonly board = computed(() => this.store.boards().find((board) => board.id === this.boardId()) ?? null);

  /** What the cross-app Knowledge and Stitch widgets key on; memoized so it changes only with the board (slides-preview `stitchSummary`). */
  protected readonly stitchSummary = computed(() => toBoardStitchSummary(this.board() ?? { id: '', title: '', archived: false, deleted: false, pinned: false, flagged: false, stitchedWith: {} }, this.spaceContext.contextId() ?? ''));
  private readonly boardSummary = computed(() => (this.board() && !isTempBoard(this.board()!.id) ? this.stitchSummary() : null));
  /** The general discussion's count: the one native count helper with Boards' scope (annotation threads are counted on their own tab). */
  private readonly commentsCount = injectBoardGeneralCommentsCount(this.boardSummary);
  protected readonly annotationList = injectBoardAnnotations(this.boardSummary);
  /** Seeded from the filters the tabs persisted, so the counts follow them before either tab opens (as Slides' editor and preview). */
  protected readonly attachmentsListState = signal(storedStitchListState(BOARD_ATTACHMENTS_MENU_STATE_VARIANT));
  protected readonly stitchListState = signal(storedStitchListState());
  private readonly attachmentsTab = viewChild(BoardAttachmentsComponent);
  /** The Attachments and Stitch counts, by the rules every item form applies: Slides' own helper, keyed on the saved board. */
  private readonly tabCounts = injectDeckTabCounts({
    item: this.boardSummary,
    hubMode: this.hasKnowledgeHubAccess,
    pendingUploads: computed(() => this.attachmentsTab()?.pendingUploads() ?? 0),
    attachmentsListState: this.attachmentsListState,
    stitchListState: this.stitchListState,
  });
  protected readonly tabs = computed(() =>
    boardTabs({ attachments: this.tabCounts.attachments(), stitch: this.tabCounts.stitch(), comments: this.commentsCount() }),
  );
  private readonly chosenTab = signal<BoardTab | undefined>(undefined);
  protected readonly activeTab = computed<BoardTab>(() => {
    const chosen = this.chosenTab();
    return this.tabs().some((tab) => tab.value === chosen) ? chosen! : 'board';
  });
  /** Tabs opened at least once stay mounted, only hidden, so their drafts outlive a tab change (R05-CX-80). */
  protected readonly visited = signal<ReadonlySet<BoardTab>>(new Set<BoardTab>(['board', 'details']));
  /** The footer's counts, from the Board tab's loaded scene; kept while another tab is open, dropped for another board. */
  protected readonly counts = signal<{ board: string; objects: number; frames: number } | null>(null);
  protected readonly footerCounts = computed(() => (this.counts()?.board === this.board()?.id ? this.counts() : null));
  protected setCounts(boardId: string, counts: { objects: number; frames: number } | null): void {
    if (counts) this.counts.set({ board: boardId, ...counts });
  }
  protected readonly displayKnowledge = computed(() => this.visibility.visible(this.scope));
  /**
   * The canvas pin of each annotation thread, for the native thread list. With no canvas here the server's own anchored or orphaned state is
   * what tells a removed target (R05-CX-83): absence of a canvas never means the target exists. Null until the board's list is read.
   */
  protected readonly threadPins = computed<ReadonlyMap<string, CommentThreadPin> | null>(() =>
    // Never classified before the first good read; after a failed refresh the last good map stays and `pinsStale` quarantines new threads (R05-CX-87/100).
    this.annotationList.loaded() ? new Map(this.annotationList.annotations().map((item) => [item.threadUid, { label: item.targetLabel, removed: annotationRemoved(item, null) }])) : null,
  );

  private readonly commentRequests = inject(BoardCommentRequestsService);
  private readonly commentTarget = inject(BoardCommentTarget);
  private readonly contentFlush = inject(BoardContentFlushService);

  public constructor() {
    // The record's own "Add comment" source: an unsaved Details draft or a failed save keeps it where it is (Slides slides-preview `commentTarget.prepareLeave`).
    this.commentTarget.prepareLeave = async () => this.canLeave() && (await this.contentFlush.flush(this.board()?.id));
    // "Add comment" from a row menu (Slides slides-preview.component.ts:282-289): the main record opens on Comments once, only for this board; a panel or modal never consumes it.
    effect(() => {
      const request = this.commentRequests.pending();
      if (!request || this.scope === ApplicationScope.DYNAMIC_PANEL || this.scope === ApplicationScope.MODAL || request.id !== this.board()?.id) return;
      untracked(() => {
        this.selectTab('comments');
        this.commentRequests.pending.set(null);
      });
    });
    // The main page's record view lends its tabs to `MP-034.open_tab` while it shows (a Dynamic Panel or window view does not).
    inject(DestroyRef).onDestroy(inject(BoardsItemTabs).attach({ boardId: () => this.board()?.id ?? null, show: (tab) => this.showTab(tab) }, this.scope));
    // The board's own markers are read once per board shown, as Slides' preview does per deck.
    let loadedFor: string | null = null;
    effect(() => {
      const id = this.board()?.id;
      if (!id || id === loadedFor || isTempBoard(id)) return;
      loadedFor = id;
      untracked(() => this.store.loadKnowledge(id));
    });
  }

  /** False while the Details hold a draft: the preview must not be left, switched or opened from over unsaved edits. */
  public canLeave(): boolean {
    const details = this.details();
    if (!details?.hasDraft()) return true;
    details.showUnsaved();
    return false;
  }

  protected selectTab(tab: BoardTab | undefined): void {
    if (tab) this.showTab(tab);
  }

  /** A tab asked for by a click or by `MP-034.open_tab`: the same guard (an unsaved Details draft vetoes the leave), and the answer says what happened. */
  protected showTab(tab: BoardTabRequest): Exclude<BoardTabOutcome, 'not_showing'> {
    // The pinned threads are the editor's panel, not a record tab (a record has none to show).
    if (tab === 'annotations') return 'no_such_tab';
    if (!this.tabs().some((candidate) => candidate.value === tab)) return 'no_such_tab';
    if (!this.canLeave()) return 'vetoed';
    this.chosenTab.set(tab);
    this.visited.update((value) => new Set([...value, tab]));
    return 'shown';
  }

  protected toggleKnowledge(): void {
    this.visibility.toggle(this.scope);
  }

  /** PAR-01/04: a native drag on the record Stitches to this board (the shared `canDropOnBoard` rule, but a Tag, Knot or Connection drag is refused here as on Slides' preview). */
  protected readonly canDropStitch = (data: DragData): boolean => {
    const board = this.board();
    return !!board && canDropOnBoard(data, { target: board, spaceId: this.spaceContext.contextId() ?? '', refuseMetadata: true });
  };

  protected dropStitch(data: DragData): void {
    const board = this.board();
    if (board) dropOnBoard(this.injector, data, board, undefined, true);
  }

  /**
   * State strip "Use template" (prototype `duplicate(true)`): the same handoff as the "From a template" dialog (`BoardCreateOptionsComponent.choose`):
   * the server's duplicate, its row made visible, then the guarded opener. Nothing is opened when the copy fails or the account changed meanwhile;
   * a copy made but not finished says so, and asking again finishes that copy instead of making another.
   */
  protected useTemplate(template: { id: string }): void {
    if (this.copying() || !this.canLeave()) return;
    const space = this.spaceContext.contextId();
    this.copying.set(true);
    this.store.duplicateBoard(template.id).subscribe((copy) => {
      this.copying.set(false);
      if (this.spaceContext.contextId() !== space) return;
      if (!copy) {
        this.toaster.show({ text: this.store.hasUnfinishedCopy(template.id) ? 'The board was made but could not be finished. Choose Use template again to finish it; no second board is made.' : 'Could not create a board from this template. Try again.' });
        return;
      }
      this.list?.ensureVisibleAfterCreate(copy.id);
      this.openBoard(copy.id);
    });
  }

  /** Prototype `navigate('all')`: the breadcrumb's "Boards app" returns to All boards with nothing selected, behind the same Details-draft guard as Close. */
  protected showAllBoards(): void {
    if (!this.canLeave()) return;
    this.list?.setView('all');
    this.store.selectBoard(null);
  }

  protected closePreview(): void {
    if (!this.canLeave()) return;
    this.closed.emit();
    this.store.selectBoard(null);
  }

  /** Open editor; from an annotation's anchor link, with that thread, so the editor shows its object, frame, element or point (R05-CX-81). */
  protected openEditor(annotationThreadUid?: string): void {
    const board = this.board();
    if (!board || !this.canLeave()) return;
    if (annotationThreadUid) this.openBoard(board.id, { annotationThreadUid });
    else this.openBoard(board.id);
  }
}
